import { useCallback, useState } from 'react'

/**
 * Measures whether a single-line heading overflows its column and
 * LATCHES the answer.
 *
 * Why measurement: the H1 compact-mode trigger used to be
 * `localName.length > 13`, but glyph width ≠ character count —
 * "Canning Town" (12 chars) overflowed the 458px column at 76px and
 * clipped under `white-space: nowrap` (found on /pl/station/940GZZLUCGT,
 * 2026-08-17). `scrollWidth > clientWidth` is the truth for any name in
 * any locale at any viewport. (The measured element also needs
 * `min-width: 0` — a flex item otherwise GROWS to its nowrap content
 * width and the overflow is invisible to scrollWidth.)
 *
 * Why the latch: the caller reacts to `overflowing` by applying the
 * compact wrapped style, after which the element no longer overflows —
 * un-latching would oscillate. The latch resets when `text` changes
 * (SPA navigation to another station).
 *
 * Contract: the caller MUST key the measured element by `text`
 * (`<h1 key={text} ref={ref}>`), so a text change remounts it and the
 * ref callback re-measures — measurement lives in the ref callback
 * (runs before paint, like a layout effect) plus resize / element-resize
 * / fonts-ready listeners, never in an effect body.
 */
export function useTitleOverflow(text: string): {
  ref: (el: HTMLHeadingElement | null) => void | (() => void)
  overflowing: boolean
} {
  const [measured, setMeasured] = useState({ text, overflowing: false })
  // Render adjustment (not an effect): new text resets the latch.
  if (measured.text !== text) setMeasured({ text, overflowing: false })

  const ref = useCallback(
    (el: HTMLHeadingElement | null) => {
      if (el === null) return
      const measure = (): void => {
        if (el.scrollWidth > el.clientWidth) {
          setMeasured((m) => (m.overflowing ? m : { text, overflowing: true }))
        }
      }
      measure()
      // The 700–900px band re-flows the column; Satoshi lands after
      // first paint and is wider than the fallback font.
      window.addEventListener('resize', measure)
      const observer =
        typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined
      observer?.observe(el)
      // jsdom has no document.fonts.
      void document.fonts?.ready.then(measure)
      return () => {
        window.removeEventListener('resize', measure)
        observer?.disconnect()
      }
    },
    [text],
  )

  return { ref, overflowing: measured.text === text && measured.overflowing }
}
